import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../utils/translations';
import api from '../utils/api';
import { Heart, CheckCircle, Loader } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import PageMeta from '../components/PageMeta';

const Donate = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const [selectedPackage, setSelectedPackage] = useState('medium');
  const [customAmount, setCustomAmount] = useState('');
  const [donorName, setDonorName] = useState('');
  const [donorEmail, setDonorEmail] = useState('');
  const [isRecurring, setIsRecurring] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);

  const packages = [
    { id: 'small', amount: 25 },
    { id: 'medium', amount: 50 },
    { id: 'large', amount: 100 },
    { id: 'generous', amount: 250 },
    { id: 'custom', amount: 0 }
  ];

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, [sessionId]);

  const checkPaymentStatus = async (sid, attempts = 0) => {
    const maxAttempts = 5;
    if (attempts >= maxAttempts) {
      setPaymentStatus('timeout');
      setCheckingPayment(false);
      return;
    }

    setCheckingPayment(true);
    try {
      const response = await api.get(`/donations/status/${sid}`);
      
      if (response.data.payment_status === 'paid') {
        setPaymentStatus('success');
        setCheckingPayment(false);
      } else if (response.data.status === 'expired') {
        setPaymentStatus('expired');
        setCheckingPayment(false);
      } else {
        // Continue polling
        setTimeout(() => checkPaymentStatus(sid, attempts + 1), 2000);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setPaymentStatus('error');
      setCheckingPayment(false);
    }
  };

  const handleDonate = async (e) => {
    e.preventDefault();
    
    // Validate custom amount if selected
    if (selectedPackage === 'custom') {
      const amount = parseFloat(customAmount);
      if (!amount || amount < 1) {
        alert(t(language, 'donate.invalidAmount'));
        return;
      }
    }
    
    setProcessing(true);

    try {
      const originUrl = window.location.origin;
      const finalPackageId = selectedPackage === 'custom' ? 'medium' : selectedPackage;
      
      const response = await api.post('/donations/checkout', null, {
        params: {
          package_id: finalPackageId,
          origin_url: originUrl,
          donor_name: donorName || undefined,
          donor_email: donorEmail || undefined,
          custom_amount: selectedPackage === 'custom' ? customAmount : undefined,
          is_recurring: isRecurring
        }
      });

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Error creating donation:', error);
      alert('Error processing donation. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  // Success page
  if (sessionId && paymentStatus === 'success') {
    const handleDownloadReceipt = () => {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      window.open(`${backendUrl}/api/donations/receipt/${sessionId}`, '_blank');
    };

    return (


      <>


        <PageMeta 


          title="Donate"


          path="/donate"


          description="Support our church building campaign. Donate via Zelle, Check, or Credit Card to help create a permanent home for our community."


        />
      <div className="min-h-screen pt-20 flex items-center justify-center bg-gradient-to-br from-amber-50 to-orange-50 dark:from-gray-900 dark:to-gray-800">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <CheckCircle className="w-24 h-24 text-green-600 dark:text-green-500 mx-auto mb-6" />
          <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4" style={{fontFamily: 'Playfair Display, serif'}}>
            {t(language, 'donate.success')}
          </h1>
          <p className="text-xl text-gray-700 dark:text-gray-300 mb-8">
            {t(language, 'donate.successMessage')}
          </p>
          
          {/* Receipt Download Button */}
          <div className="mb-6">
            <Button
              onClick={handleDownloadReceipt}
              className="px-8 py-3 bg-green-600 hover:bg-green-700 text-white rounded-full mr-4"
              data-testid="download-receipt-btn"
            >
              📄 {t(language, 'donate.downloadReceipt')}
            </Button>
          </div>

          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            {language === 'en' 
              ? 'A copy of your receipt has been sent to your email (if provided)'
              : 'ናይ ደገፍኩም ቅብሊት ቅዳሕ ናብ ኢመይልኩም ተለኢኹ ኣሎ'
            }
          </p>

          <Button
            onClick={() => navigate('/donate')}
            className="px-8 py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-full"
            data-testid="back-to-donate-btn"
          >
            {t(language, 'donate.backToDonate')}
          </Button>
        </div>
      </div>
    </>
  );
  }

  // Processing page
  if (sessionId && checkingPayment) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center bg-gradient-to-br from-amber-50 to-orange-50 dark:from-gray-900 dark:to-gray-800">
        <div className="text-center">
          <Loader className="w-16 h-16 text-amber-600 animate-spin mx-auto mb-4" />
          <p className="text-xl text-gray-700 dark:text-gray-300">
            {t(language, 'donate.processing')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="bg-gradient-to-br from-amber-100 via-orange-50 to-rose-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Heart className="w-16 h-16 text-amber-600 dark:text-amber-500 mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4" style={{fontFamily: 'Playfair Display, serif'}}>
            {t(language, 'donate.title')}
          </h1>
          <p className="text-lg text-gray-700 dark:text-gray-300">
            {t(language, 'donate.subtitle')}
          </p>
        </div>
      </section>

      {/* Payment Methods Section - Moved to Top */}
      <section className="py-12 bg-gradient-to-br from-green-50 to-blue-50 dark:from-gray-800 dark:to-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-8 text-center" style={{fontFamily: 'Playfair Display, serif'}}>
            {language === 'en' ? 'Ways to Give' : 'መንገዲ ልግሲ'}
          </h2>
          
          <div className="space-y-6">
            {/* Zelle - Recommended */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-2 border-green-400 dark:border-green-600 rounded-xl p-6 relative">
              <div className="absolute top-4 right-4 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                {language === 'en' ? '⭐ RECOMMENDED' : '⭐ ተመራጺ መንገዲ'}
              </div>
              <div className="mb-4">
                <h4 className="text-2xl font-bold text-green-800 dark:text-green-400 mb-2">
                  {language === 'en' ? 'Zelle' : 'ብዘል (Zelle)'}
                </h4>
              </div>
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 mb-3">
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                  {language === 'en' ? 'Send to:' : 'ናብዚ ኢመይል ለኣኽ:'}
                </p>
                <p className="text-xl font-bold text-gray-900 dark:text-white break-all">
                  mheeotcdallas@gmail.com
                </p>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 italic">
                {language === 'en' 
                  ? '💡 Tip: Add a note mentioning your name and "Church Donation"' 
                  : '💡 ምኽሪ: ስምኻን "ናይ ቤተ ክርስትያን ልግሲ" ዝብል መልእኽቲ ወስኽ'}
              </p>
            </div>

            {/* Check Payment */}
            <div className="bg-amber-50 dark:bg-gray-800 border-2 border-amber-300 dark:border-amber-700 rounded-xl p-6">
              <h4 className="text-xl font-bold text-amber-800 dark:text-amber-400 mb-4">
                {language === 'en' ? 'Check / Mail' : 'ብቸክ (Check)'}
              </h4>
              <div className="space-y-3">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                    {language === 'en' ? 'Make check payable to:' : 'ተከፋሊ ናብ:'}
                  </p>
                  <p className="font-bold text-gray-900 dark:text-white">
                    Mekane Hiwet Eyesus EOT Church
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                    {language === 'en' ? 'Mail to:' : 'Address'}
                  </p>
                  <p className="text-gray-900 dark:text-white">
                    P.O. Box 452017<br/>
                    Garland, TX 75044
                  </p>
                </div>
              </div>
            </div>

            {/* Online Donation Option */}
            <div className="bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-300 dark:border-blue-700 rounded-xl p-6 text-center">
              <h4 className="text-xl font-bold text-blue-800 dark:text-blue-400 mb-2">
                {language === 'en' ? '💳 Online Credit/Debit Card' : '💳 ብካርድ (ክሬዲት/ዴቢት)'}
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                {language === 'en' 
                  ? 'Use the form below for secure online donations' 
                  : 'ኣብ ታሕቲ ዘሎ ፎርም ተጠቐም ንውሑስ ኦንላይን ልግሲ'}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-500">
                ✨ {language === 'en' 
                  ? 'All donations are tax-deductible. You will receive a receipt.' 
                  : 'ኩሉ ደገፍ ካብ ቀረጽ ነጻ እዩ። ቅብሊት ናይ ደገፍኩም ክትረኽቡ ኢኹም።'}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Donation Form */}
      <section className="py-16 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleDonate}>
            {/* Package Selection */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6" style={{fontFamily: 'Playfair Display, serif'}}>
                {t(language, 'donate.selectAmount')}
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                {packages.map((pkg) => (
                  <Card
                    key={pkg.id}
                    onClick={() => {
                      setSelectedPackage(pkg.id);
                      if (pkg.id !== 'custom') setCustomAmount('');
                    }}
                    className={`p-6 cursor-pointer transition-all duration-300 ${
                      selectedPackage === pkg.id
                        ? 'bg-amber-600 text-white shadow-xl scale-105'
                        : 'bg-white dark:bg-gray-800 hover:shadow-lg'
                    }`}
                    data-testid={`package-${pkg.id}`}
                  >
                    <div className="text-center">
                      {pkg.id === 'custom' ? (
                        <>
                          <p className="text-2xl font-bold mb-2">
                            {t(language, 'donate.packages.other')}
                          </p>
                          <p className="text-xs">
                            {t(language, 'donate.packages.customAmount')}
                          </p>
                        </>
                      ) : (
                        <p className="text-3xl font-bold">${pkg.amount}</p>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
              
              {/* Custom Amount Input */}
              {selectedPackage === 'custom' && (
                <div className="mt-6 max-w-md mx-auto">
                  <label className="block text-sm font-semibold mb-2 text-gray-700 dark:text-gray-300">
                    {t(language, 'donate.enterAmount')}
                  </label>
                  <Input
                    type="number"
                    min="1"
                    step="1"
                    placeholder={t(language, 'donate.enterAmountPlaceholder')}
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    className="w-full px-4 py-3 text-lg border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-amber-500 focus:ring-2 focus:ring-amber-200"
                    required
                  />
                </div>
              )}
            </div>

            {/* Donor Information */}
            <div className="mb-8">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
                {t(language, 'donate.donorInfo')}
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <Input
                  type="text"
                  placeholder={t(language, 'donate.name')}
                  value={donorName}
                  onChange={(e) => setDonorName(e.target.value)}
                  className="py-3"
                  data-testid="donor-name-input"
                />
                <Input
                  type="email"
                  placeholder={t(language, 'donate.email')}
                  value={donorEmail}
                  onChange={(e) => setDonorEmail(e.target.value)}
                  className="py-3"
                  data-testid="donor-email-input"
                />
              </div>
            </div>

            {/* Monthly Recurring Payment Option */}
            <div className="mb-8">
              <label className="flex items-center space-x-3 p-4 border-2 border-amber-200 dark:border-amber-800 rounded-lg hover:border-amber-400 dark:hover:border-amber-600 transition-colors cursor-pointer">
                <input
                  type="checkbox"
                  checked={isRecurring}
                  onChange={(e) => setIsRecurring(e.target.checked)}
                  className="w-5 h-5 text-amber-600 border-gray-300 rounded focus:ring-amber-500 cursor-pointer"
                  data-testid="recurring-checkbox"
                />
                <div className="flex-1">
                  <span className="text-lg font-semibold text-gray-900 dark:text-white">
                    {t(language, 'donate.monthlyRecurring')}
                  </span>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {language === 'en' 
                      ? 'Your card will be charged automatically each month'
                      : 'ካርድኩም ኣብ ዅሉ ወርሒ ብኣውቶማቲክ ክኽፈል እዩ'
                    }
                  </p>
                </div>
              </label>
            </div>

            {/* Submit Button */}
            <div className="text-center">
              <Button
                type="submit"
                disabled={processing}
                className="px-12 py-4 bg-amber-600 hover:bg-amber-700 text-white text-lg rounded-full shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50"
                data-testid="submit-donation-btn"
              >
                {processing ? (
                  <Loader className="w-6 h-6 animate-spin" />
                ) : (
                  isRecurring 
                    ? t(language, 'donate.startMonthlyTithe')
                    : t(language, 'donate.submit')
                )}
              </Button>
            </div>
          </form>

          {/* Church Building Campaign */}
          <div className="mt-12 pt-8 border-t border-gray-200 dark:border-gray-700">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 text-center" style={{fontFamily: 'Playfair Display, serif'}}>
              {language === 'en' ? 'Why We Need Your Support' : 'ደገፍኩምን ሓገዝኩምን ንደሊ'}
            </h2>
            
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-gray-800 dark:to-gray-700 rounded-2xl p-8 mb-8">
              <h3 className="text-2xl font-bold text-amber-800 dark:text-amber-400 mb-4">
                {language === 'en' ? 'Church Building Campaign' : 'ሓገዝ ንህንጻ ቤተ ክርስትያን'}
              </h3>
              
              <div className="space-y-4 text-gray-700 dark:text-gray-300">
                <div>
                  <h4 className="font-bold text-lg mb-2">{language === 'en' ? 'Current Challenges:' : 'ዘለዉና ሕጽረታት:'}</h4>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>{language === 'en' ? 'Our space is becoming increasingly inadequate ' : 'ዘለና ክፍሊ ኣካሊ ብዘይ ሙካኑ'}</li>
                    <li>{language === 'en' ? 'Borrowed facilities are not sustainable long-term' : 'ንጥቀመሉ ዘለና ህንጻ ካብ ኣሕዋት ግብጻውያን ዝተረክበ እዩ'}</li>
                    <li>{language === 'en' ? 'Limited time for lessons and lack of classroom space' : 'ግዜ ትምህርትን ቦታን ውሱን ኮይኑ'}</li>
                    <li>{language === 'en' ? 'Growing congregation needs unrestricted services' : 'እናዓበየ ዝከይድ ዘሎ ኣገልግሎት ኣኩል ዝኮነ ቦታ የድልዮ'}</li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="font-bold text-lg mb-2">{language === 'en' ? 'Our Building Will Provide:' : 'ቤት-ክርስትያና እትህቦ ኣገልግሎት:'}</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <p className="font-semibold text-amber-700 dark:text-amber-400">
                        {language === 'en' ? '🏛️ Church Hall' : '🏛️ ሓፈሻዊ ኣገልግሎት'}
                      </p>
                      <p className="text-sm">{language === 'en' ? 'For weddings, baptisms, memorials, and celebrations' : 'ንመርዓ፡ ጥምቐት፡ ሓዝን፡ በዓላት'}</p>
                    </div>
                    <div>
                      <p className="font-semibold text-amber-700 dark:text-amber-400">
                        {language === 'en' ? '👶 Youth Ministry' : '👶 ናይ ህጻናትን መንእሰያትን አገልግሎት'}
                      </p>
                      <p className="text-sm">{language === 'en' ? 'Religious education in English, play areas' : 'ብእንግሊዝኛ መንፈሳዊ ትምህርቲ፡ ንህጻናት መጻወትን መዘናግዕን'}</p>
                    </div>
                    <div>
                      <p className="font-semibold text-amber-700 dark:text-amber-400">
                        {language === 'en' ? '📚 library' : '📚  ቤት ንባብ'}
                      </p>
                      <p className="text-sm">{language === 'en' ? 'Classrooms for all age groups' : 'ንኩሉ ዕድመ ክፍሊ ትምህርቲ'}</p>
                    </div>
                    <div>
                      <p className="font-semibold text-amber-700 dark:text-amber-400">
                        {language === 'en' ? '🙏 Prayer Sanctuary' : '🙏 ቦታ ጸሎት'}
                      </p>
                      <p className="text-sm">{language === 'en' ? 'Dedicated worship space for our community' : 'ንማሕበርና ዝተወፈየ ቦታ አምልኾ'}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white dark:bg-gray-900 rounded-xl p-6 mt-6">
                  <p className="text-center italic text-gray-600 dark:text-gray-400">
                    {language === 'en' 
                      ? '"My God will supply every need of yours according to his riches in glory in Christ Jesus." - Philippians 4:19'
                      : '"ኣምላኸይ ከኣ፡ ከምቲ መጠን ሃብቱ ብኽብሪ ብክርስቶስ የሱስ ገይሩ፡ ድሌትኩም ዘበለ ኺመልኣልኩም እዩ።" - ፊልጵ 4፡19'
                    }
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Donate;
